<template>
  <ViewContainer :title="$t('profile_settings.edit_profile')">
    <v-row>
      <v-col cols="12" sm="12" md="4" lg="3" class="mb-5">
        <v-sheet elevation="2" class="rounded pa-2">
          <v-list nav>
            <v-list-item-group
              mandatory
              v-model="option"
              data-testid="profileSettings-list-menu"
              color="primary"
            >
              <v-list-item v-for="(option, i) in options" :key="i">
                <v-list-item-content :data-tourid="option.tourid">
                  <v-list-item-title
                    class="px-3"
                    v-text="option.text"
                  ></v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-sheet>
      </v-col>
      <v-col v-if="account._id" cols="12" sm="12" md="8" lg="7">
        <Profile
          v-show="option === OPTIONS.PROFILE"
          data-testid="profileSettings-profile"
          :account="account"
          :isLoading="isLoading"
          :showDialog="showDialog"
          @update="updateAccount"
          @reset="resetChanges"
          @accountChanged="accountHasChanged"
          @cancelLeaveRoute="cancelLeaveRoute"
        />
        <Skills
          v-show="option === OPTIONS.SKILLS"
          data-testid="profileSettings-skills"
          :skills="account.skills"
          :isLoading="isLoading"
          @update="updateAccount"
        />
        <Notifications
          v-show="option === OPTIONS.NOTIFICATIONS"
          data-testid="profileSettings-notifications"
          :account="account"
          :isLoading="isLoading"
          @update="updateAccount"
        />
        <ChangePassword
          v-show="isChangePasswordVisible"
          data-testid="profileSettings-ChangePassword"
          :isLoading="isLoading"
          :onUpdate="updatePassword"
        />
      </v-col>
    </v-row>
  </ViewContainer>
</template>

<script>
import ViewContainer from '@/components/Core/Layout/ViewContainer.vue'
import Profile from './Profile'
import Skills from './Skills'
import ChangePassword from './ChangePassword'
import Notifications from './Notifications'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import { mapActions } from 'vuex'
import _ from 'lodash'

const OPTIONS = {
  PROFILE: 0,
  SKILLS: 1,
  NOTIFICATIONS: 2,
  CHANGE_PASSWORD: 3,
}

const HTTP_PWD_ERROR = 406

export default {
  name: 'ProfileSettingsView',

  mixins: [mixin, trackerMixin],

  components: {
    ViewContainer,
    Profile,
    Skills,
    ChangePassword,
    Notifications,
  },

  data() {
    return {
      option: 0,
      isLoading: true,
      showDialog: false,
      accountChanged: false,
      router: null,
      account: {},
      OPTIONS,
    }
  },

  mounted() {
    this.getAccount()

    const routeOption = this.$route?.query?.option
    if (routeOption) {
      this.option = this.OPTIONS[routeOption.toUpperCase()]
    }
  },

  computed: {
    options() {
      return _.compact([
        {
          number: OPTIONS.PROFILE,
          text: this.$t('profile_settings.personal_information'),
          tourid: 'profileSettings-option-profile',
        },
        {
          number: OPTIONS.SKILLS,
          text: this.$t('profile_settings.skill_other'),
          tourid: 'profileSettings-option-skills',
        },
        {
          number: OPTIONS.NOTIFICATIONS,
          text: this.$t('notifications.notification_other'),
          tourid: 'profileSettings-option-notifications',
        },
        this.canChangePassword && {
          number: OPTIONS.CHANGE_PASSWORD,
          text: this.$t('auth.lost_password.change_password'),
          tourid: 'profileSettings-option-changePassword',
        },
      ])
    },

    canChangePassword() {
      const authType = this.account?.organization?.authenticationType ?? ''
      const isPrimaryAccount = this.account?.isPrimary !== false
      return (
        isPrimaryAccount &&
        !this.$constants.thirdPartyAuthProviders.includes(authType)
      )
    },

    isChangePasswordVisible() {
      return (
        this.option === OPTIONS.CHANGE_PASSWORD && this.canChangePassword
      )
    },
  },

  methods: {
    ...mapActions({
      get: 'profileSettings/getAccount',
      update: 'updateOwnAccount',
    }),

    async getAccount() {
      this.isLoading = true
      try {
        const response = await this.get()

        this.account = response.data

        const isRestrictedPanelSelected =
          this.option === OPTIONS.CHANGE_PASSWORD && !this.canChangePassword

        if (isRestrictedPanelSelected) this.option = OPTIONS.PROFILE
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    async updateAccount(updatedData) {
      try {
        this.isLoading = true

        await this.update({
          organizationId: this.$store.getters.organizationId,
          userId: this.account._id,
          data: { ...this.account, ...updatedData },
        })

        await this.getAccount()

        this.$resetTracking()
        this.$configureTracking()

        this.showSuccess(this.$t('profile_settings.successfully_saved'))
        this.accountChanged = false

        if (this.router?.next) this.router.next()
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
        this.showDialog = false
      }
    },

    async updatePassword(updatedData) {
      try {
        this.isLoading = true
        await api.account.changePassword(
          this.$store.getters.organizationId,
          this.account._id,
          updatedData
        )
        this.showSuccess(this.$t('profile_settings.successfully_saved'))
        return true
      } catch (err) {
        if (err?.response?.status === HTTP_PWD_ERROR)
          this.showError(this.$t('profile_settings.current_passwort_not_match'))
        else this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    accountHasChanged(value) {
      this.accountChanged = value
    },

    resetChanges() {
      this.account = { ...this.originalAccount }
      this.accountChanged = false
    },

    cancelLeaveRoute() {
      this.showDialog = false
      this.router = null
    },
  },

  beforeDestroy() {
    this.account = {}
  },

  beforeRouteLeave(to, from, next) {
    if (!this.accountChanged) return next()

    this.showDialog = true
    this.router = { to, from, next }
    return next(false)
  },
}
</script>
